// Ubicación en el repo: src/lib/privacyPolicy.js
// Política de Privacidad — pedido de Nicolás (22-ago-2026, ver DECISIONS.md):
// se muestra en el footer de /agendamiento-online junto al consentimiento
// informado (ver PublicScheduling.jsx). Adaptada de
// `Politica-Privacidad-y-Consentimiento-borrador.docx` (Parte I, borrador ya
// existente en la raíz del repo, armado en una sesión anterior con fuentes
// públicas — ver ese documento y el RAT para el detalle de fuentes), con los
// datos de la clínica ahora dinámicos (vía `clinic_settings`, migración
// 0040/0041) en vez de campos entre corchetes por completar a mano.
//
// Es un borrador técnico — quien lo valide legalmente antes de que este
// texto sea definitivo es el asesor de protección de datos pendiente de
// contratar (Fase 0, ver todo-pendiente-maestro.md). Ese aviso NO se
// muestra al paciente (rompería la confianza en una página pública en vivo)
// — queda documentado acá y en DECISIONS.md/todo-pendiente-maestro.md.
//
// Mismo patrón que consentDocument.js: `req()` imprime un aviso
// `[completar...]` en vez de inventar un dato si Configuración → Datos
// legales todavía está vacío.

export const PRIVACY_POLICY_VERSION = '2026-08-22';

function req(value, label) {
  return value?.trim() ? value : `[${label} — completar en Configuración → Datos legales]`;
}

// Devuelve las secciones como texto plano estructurado (título + párrafos),
// para poder renderizarlas tanto en el footer de React (mapeando <p> por
// párrafo) como, más adelante si hiciera falta, en un documento imprimible
// — sin duplicar el contenido en dos formatos distintos.
export function getPrivacyPolicySections(clinicLegal) {
  const razonSocial = req(clinicLegal?.razonSocial, 'razón social');
  const rut = req(clinicLegal?.rut, 'RUT');
  const direccion = req(clinicLegal?.direccion, 'dirección');
  const correoContacto = req(clinicLegal?.correoContactoDatos, 'correo de contacto');

  return [
    {
      title: '1. Quiénes somos',
      paragraphs: [
        `${razonSocial}, RUT ${rut}, con domicilio en ${direccion}, en adelante "nosotros" o "la Clínica", operamos el sistema Agenda Comunal para la gestión de atenciones de audiología/otorrinolaringología y oftalmología en nuestras sucursales. Esta política explica qué datos personales recopilamos, para qué los usamos, con quién los compartimos y qué derechos tiene usted sobre ellos.`,
        `Contacto para temas de privacidad: ${correoContacto}.`,
      ],
    },
    {
      title: '2. Qué datos recopilamos',
      paragraphs: [
        'Datos de identificación: nombre completo, RUT, fecha de nacimiento.',
        'Datos de contacto: dirección, teléfono, correo electrónico.',
        'Datos de salud (dato sensible): resultados de exámenes (audiometrías, agudeza visual), diagnósticos, recetas, productos indicados o vendidos (audífonos, lentes), notas clínicas.',
        'Datos de la atención: fecha y tipo de citas, sucursal y profesional que atiende.',
        'Si usted es profesional o colaborador de la Clínica: además de lo anterior, su rol, sucursal(es) asignadas, y credenciales de acceso al sistema.',
      ],
    },
    {
      title: '3. Para qué usamos sus datos',
      paragraphs: [
        'Brindarle atención de salud: registrar su ficha, sus exámenes, sus recetas y hacer seguimiento de su tratamiento.',
        'Coordinar su agenda: citas, recordatorios, reagendamientos, incluido el auto-agendamiento en línea.',
        'Cumplir obligaciones legales, como la conservación de la ficha clínica por el plazo que exige la normativa de salud.',
        'Comunicarnos con usted por correo o teléfono cuando sea necesario para su atención.',
      ],
    },
    {
      title: '4. Base legal del tratamiento',
      paragraphs: [
        'La atención clínica en sí (ficha, examen, receta) se realiza al amparo de la excepción de "prestación de servicios de salud" que contempla la Ley 21.719, en concordancia con la Ley 20.584 — no requiere un consentimiento adicional para el tratamiento terapéutico propiamente tal. Para el tratamiento de sus datos personales en general, más allá de lo estrictamente terapéutico, le solicitamos su consentimiento explícito — ver el Consentimiento Informado, junto a esta política.',
      ],
    },
    {
      title: '5. Con quién compartimos sus datos y transferencia internacional',
      paragraphs: [
        'No vendemos sus datos personales ni los compartimos con fines de publicidad. Los compartimos únicamente con los proveedores tecnológicos que usamos para operar el sistema, quienes actúan como "encargados de tratamiento" bajo nuestras instrucciones:',
        'Supabase — base de datos, autenticación y almacenamiento de archivos. Datos alojados en São Paulo, Brasil.',
        'Resend — envío de correos operativos (confirmaciones, recordatorios, invitaciones, restablecimiento de contraseña). Datos alojados en Estados Unidos.',
        'Netlify — alojamiento del sitio web de la aplicación. Infraestructura en Estados Unidos.',
        'Como estos proveedores están fuera de Chile, existe una transferencia internacional de sus datos, informada aquí conforme a la Ley 21.719. Contamos con acuerdos de tratamiento de datos con los tres, con cláusulas para proteger la información durante esa transferencia.',
      ],
    },
    {
      title: '6. Cuánto tiempo conservamos sus datos',
      paragraphs: [
        'Ficha clínica y todo lo asociado a su atención: mínimo 15 años desde el último registro en su ficha (Decreto 41 MINSAL / Ley 20.584).',
        'Datos de agenda y contacto: mientras dure su relación con nosotros como paciente, y luego junto con su ficha clínica.',
        'Para el resto de sus datos personales, por el plazo que establezca la Ley 21.719 desde su entrada en vigencia el 1 de diciembre de 2026.',
      ],
    },
    {
      title: '7. Sus derechos',
      paragraphs: [
        `Usted tiene los siguientes derechos sobre sus datos personales, que puede ejercer contactándonos a ${correoContacto}:`,
        'Acceso: saber qué datos suyos tenemos, de dónde vienen, para qué los usamos y con quién los compartimos.',
        'Rectificación: pedir que corrijamos datos inexactos, desactualizados o incompletos.',
        'Supresión (cancelación): pedir que eliminemos sus datos cuando ya no sean necesarios — sujeto a la obligación legal de conservar la ficha clínica por el plazo indicado arriba.',
        'Oposición: oponerse a un tratamiento específico de sus datos, cuando la ley lo permita.',
        'Portabilidad: pedir una copia de sus datos en un formato electrónico que se pueda transferir a otro prestador.',
        'Bloqueo: pedir que suspendamos temporalmente el tratamiento de sus datos mientras resolvemos una solicitud de rectificación, supresión u oposición.',
        'Responderemos su solicitud dentro del plazo que establece la Ley 21.719.',
      ],
    },
    {
      title: '8. Seguridad',
      paragraphs: [
        'Protegemos sus datos con control de acceso por sucursal y por rol, autenticación de doble factor para el personal con acceso a datos clínicos, conexión cifrada en todas las comunicaciones, y respaldos periódicos. Nunca eliminamos físicamente una ficha clínica, por la obligación legal de conservación.',
      ],
    },
    {
      title: '9. Reclamos',
      paragraphs: [
        `Si no está conforme con cómo tratamos sus datos, puede reclamar directamente con nosotros a ${correoContacto}, o ante la Agencia de Protección de Datos Personales una vez que esta entre en funcionamiento (1 de diciembre de 2026).`,
      ],
    },
    {
      title: '10. Cambios a esta política',
      paragraphs: [
        'Podemos actualizar esta política cuando cambien nuestras prácticas o la normativa aplicable. Publicaremos la versión vigente en la aplicación, con la fecha de la última actualización.',
      ],
    },
  ];
}
